// Pure client-side mirror of household_access() and has_plus() in
// 042_free_plus.sql — for UI only (which banner to show, when a tap opens the
// subscribe sheet instead of trying). It is NOT the enforcement: RLS refuses a
// Plus write from a free household with the same rule, the item trigger refuses
// the sixteenth open item, and the recipe and watch functions ask has_plus
// themselves. A user could patch this in devtools and see a stale UI, but every
// write still goes through the real check.

import type { Household } from './supabase'
import { FREE_ITEM_LIMIT, GRACE_DAYS } from './pricing'

/** Plus in the first three; Free in the last. */
export type Access = 'trialing' | 'active' | 'grace' | 'free'

/** What a tap on a Plus feature was after — the subscribe sheet leads with it. */
export type PlusReason = 'list_full' | 'recipes' | 'planning' | 'memory' | 'store' | 'stats' | 'watch'

const DAY_MS = 86_400_000

export function householdAccess(household: Household, now: number = Date.now()): Access {
  // A deployment that stopped its migrations before 031 has no plan_expires_at,
  // so Postgres hands back undefined. Fail open rather than locking every
  // household on a deployment that has no way to pay.
  if (household.subscription_status == null || household.plan_expires_at == null) return 'active'
  const expires = Date.parse(household.plan_expires_at)
  const status = household.subscription_status
  if (status === 'active' && expires > now) return 'active'
  if (status === 'trialing') return expires > now ? 'trialing' : 'free'
  return expires + GRACE_DAYS * DAY_MS > now ? 'grace' : 'free'
}

export function hasPlus(household: Household, now?: number): boolean {
  return householdAccess(household, now) !== 'free'
}

/** Is a subscription live with Stripe (so /checkout would answer 409)? The payer's portal is then the way in. */
export function hasSubscription(household: Household): boolean {
  return household.subscription_status === 'active' || household.subscription_status === 'past_due'
}

/** Whole days left in the trial, rounded up — 0 once it has ended or isn't a trial. */
export function trialDaysLeft(household: Household, now: number = Date.now()): number {
  if (householdAccess(household, now) !== 'trialing') return 0
  return Math.max(0, Math.ceil((Date.parse(household.plan_expires_at) - now) / DAY_MS))
}

/** When a household in grace drops to Free. */
export function graceEndsAt(household: Household): Date {
  return new Date(Date.parse(household.plan_expires_at) + GRACE_DAYS * DAY_MS)
}

/** Places left on a free list with `open` items on it; Infinity with Plus. */
export function freeItemsLeft(household: Household, open: number, now?: number): number {
  if (hasPlus(household, now)) return Infinity
  return Math.max(0, FREE_ITEM_LIMIT - open)
}

/**
 * Did the server refuse a write because it needs Plus? RLS raises SQLSTATE
 * 42501 (insufficient_privilege), which PostgREST hands back as a 403 with that
 * code — the same shape rpcError.ts matches PT429 on.
 */
export function isPlusRequiredError(err: unknown): boolean {
  return codeOf(err) === '42501'
}

/** Did the server refuse an open item because the free list is full (items_free_limit_guard, PT413)? */
export function isListFullError(err: unknown): boolean {
  return codeOf(err) === 'PT413'
}

function codeOf(err: unknown): unknown {
  return typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : undefined
}

/**
 * The one line under the list's hero, or null while paid. It says what to do,
 * never the hero's own number again (ux/01).
 */
export function planCaption(household: Household, now: number = Date.now()): string | null {
  switch (householdAccess(household, now)) {
    case 'trialing': {
      const days = trialDaysLeft(household, now)
      return `${days} ${days === 1 ? 'day' : 'days'} left in your trial`
    }
    case 'grace': return 'Payment issue. Renew to keep Plus'
    case 'free': return `Free plan: up to ${FREE_ITEM_LIMIT} items`
    default: return null
  }
}
